import React, { useState } from 'react';
import axios from 'axios';
import {
  Kanban, Plus, Trash2, Pencil, Save, Flag, ChevronLeft, ChevronRight, Clock, AlertCircle
} from 'lucide-react';
import { Modal, getRoleLabel, confirmDialog } from './shared';

export default function KanbanTab({ user, authHeaders, API_URL, kanbanTasks, fetchKanban, allUsers }) {
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({ description: '', priority: '', deadline: '', assignee_id: '' });
  const [newUpdate, setNewUpdate] = useState('');
  const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'normal', deadline: '', assignee_id: '' });
  const [draggedTask, setDraggedTask] = useState(null);
  const [kanbanAssigneeFilter, setKanbanAssigneeFilter] = useState('all');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const adminCoachUsers = allUsers.filter(u => u.status === 'active' && (u.role === 'admin' || u.role === 'coach'));

  const columns = [
    { id: 'neu', label: 'Neu', color: '#C8A45C' },
    { id: 'in_arbeit', label: 'In Arbeit', color: '#FFD700' },
    { id: 'erledigt', label: 'Erledigt', color: '#C8A45C' },
  ];
  const priorityColors = { hoch: '#FF0055', normal: '#FFD700', niedrig: '#8C92AC' };
  const priorityLabels = { hoch: 'Hoch', normal: 'Normal', niedrig: 'Niedrig' };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setFormLoading(true); setFormError('');
    try {
      await axios.post(`${API_URL}/api/community/kanban/tasks`, taskForm, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setTaskForm({ title: '', description: '', priority: 'normal', deadline: '', assignee_id: '' });
      setShowCreateTask(false);
      fetchKanban();
    } catch (e) { setFormError(e.response?.data?.detail || 'Fehler'); }
    setFormLoading(false);
  };

  const moveTask = async (taskId, newStatus) => {
    const task = kanbanTasks.find(t => t.id === taskId);
    if (task && task.status === 'erledigt') return;
    try {
      await axios.put(`${API_URL}/api/community/kanban/tasks/${taskId}`, { task_status: newStatus }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      if (newStatus === 'erledigt' && task) {
        try {
          await axios.post(`${API_URL}/api/community/changelog`, {
            title: `Task erledigt: ${task.title}`,
            content: `Der Task "${task.title}" wurde als erledigt markiert.${task.assignee_name ? ' Zugeordnet: ' + task.assignee_name : ''}`,
            category: 'Allgemein',
            confidential: false,
          }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
        } catch (e2) { console.error('Changelog entry failed:', e2); }
      }
      fetchKanban();
    } catch (e) { console.error(e); }
  };

  const handleDeleteTask = async (taskId) => {
    if (!(await confirmDialog('Aufgabe wirklich löschen?', { title: 'Aufgabe löschen' }))) return;
    try {
      await axios.delete(`${API_URL}/api/community/kanban/tasks/${taskId}`, { headers: authHeaders });
      fetchKanban();
    } catch (e) { console.error(e); }
  };

  const handleDragStart = (e, taskId) => { setDraggedTask(taskId); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (e, targetStatus) => {
    e.preventDefault();
    if (draggedTask) {
      const task = kanbanTasks.find(t => t.id === draggedTask);
      if (task && task.status === 'erledigt') { setDraggedTask(null); return; }
      moveTask(draggedTask, targetStatus);
      setDraggedTask(null);
    }
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setEditTaskForm({ description: task.description || '', priority: task.priority || 'normal', deadline: task.deadline || '', assignee_id: task.assignee_id || '' });
    setNewUpdate('');
    setShowEditTask(true);
    setFormError('');
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    setFormLoading(true); setFormError('');
    try {
      const updatePayload = {};
      if (editTaskForm.description !== editingTask.description) updatePayload.description = editTaskForm.description;
      if (editTaskForm.priority !== editingTask.priority) updatePayload.priority = editTaskForm.priority;
      if (editTaskForm.deadline !== (editingTask.deadline || '')) updatePayload.deadline = editTaskForm.deadline;
      if (editTaskForm.assignee_id !== (editingTask.assignee_id || '')) updatePayload.assignee_id = editTaskForm.assignee_id;
      if (Object.keys(updatePayload).length > 0) {
        await axios.put(`${API_URL}/api/community/kanban/tasks/${editingTask.id}`, updatePayload, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      }
      if (newUpdate.trim()) {
        await axios.post(`${API_URL}/api/community/kanban/tasks/${editingTask.id}/updates`, { text: newUpdate.trim() }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      }
      setShowEditTask(false);
      fetchKanban();
    } catch (e) { setFormError(e.response?.data?.detail || 'Fehler'); }
    setFormLoading(false);
  };

  return (
    <div className="tab-content" data-testid="kanban-content">
      <div className="section-header">
        <h2><Kanban size={20} /> Kanban Board</h2>
        <div style={{display: 'flex', gap: '0.75rem', alignItems: 'center'}}>
          <select className="modal-select" value={kanbanAssigneeFilter} onChange={e => setKanbanAssigneeFilter(e.target.value)} style={{minWidth: '160px'}} data-testid="kanban-filter">
            <option value="all">Alle Personen</option>
            <option value="unassigned">Nicht zugeordnet</option>
            {[...new Set(kanbanTasks.filter(t => t.assignee_name).map(t => t.assignee_name))].sort().map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <button onClick={() => { setTaskForm({ title: '', description: '', priority: 'normal', deadline: '', assignee_id: '' }); setShowCreateTask(true); setFormError(''); }} className="btn-sm" data-testid="create-task-btn"><Plus size={16} /> Neuer Task</button>
        </div>
      </div>
      <div className="kanban-board" data-testid="kanban-board">
        {columns.map(col => {
          const colTasks = kanbanTasks.filter(t => t.status === col.id && (
            kanbanAssigneeFilter === 'all' ||
            (kanbanAssigneeFilter === 'unassigned' && !t.assignee_name) ||
            t.assignee_name === kanbanAssigneeFilter
          ));
          return (
            <div key={col.id} className="kanban-column" onDragOver={handleDragOver} onDrop={e => handleDrop(e, col.id)} data-testid={`kanban-col-${col.id}`}>
              <div className="kanban-col-header" style={{ borderBottomColor: col.color }}>
                <span className="kanban-col-title" style={{ color: col.color }}>{col.label}</span>
                <span className="kanban-col-count">{colTasks.length}</span>
              </div>
              <div className="kanban-col-body">
                {colTasks.map(task => (
                  <div key={task.id} className={`kanban-card ${task.status === 'erledigt' ? 'kanban-card-done' : ''}`} draggable={task.status !== 'erledigt'} onDragStart={e => task.status !== 'erledigt' && handleDragStart(e, task.id)} onClick={() => openEditTask(task)} data-testid={`kanban-task-${task.id}`}>
                    <div className="kanban-card-header">
                      <span className="kanban-card-title">{task.title}</span>
                      <button className="btn-icon-sm danger" onClick={e => { e.stopPropagation(); handleDeleteTask(task.id); }} data-testid={`del-task-${task.id}`}><Trash2 size={12} /></button>
                    </div>
                    {task.description && <p className="kanban-card-desc">{task.description}</p>}
                    <div className="kanban-card-footer">
                      <span className="kanban-priority" style={{ color: priorityColors[task.priority] || '#8C92AC' }}>
                        <Flag size={11} /> {priorityLabels[task.priority] || task.priority}
                      </span>
                      {task.assignee_name && <span className="kanban-assignee">{task.assignee_name}</span>}
                    </div>
                    {task.updates && task.updates.length > 0 && <div className="kanban-update-count">{task.updates.length} Update{task.updates.length > 1 ? 's' : ''}</div>}
                    {task.deadline && (() => {
                      const dl = new Date(task.deadline);
                      const now = new Date();
                      const diff = Math.ceil((dl - now) / 86400000);
                      const isOverdue = diff < 0;
                      const isSoon = diff >= 0 && diff <= 3;
                      return <div className={`kanban-deadline ${isOverdue ? 'overdue' : isSoon ? 'soon' : ''}`}><Clock size={11} /> {dl.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</div>;
                    })()}
                    <div className="kanban-card-move">
                      {col.id !== 'neu' && task.status !== 'erledigt' && <button className="kanban-move-btn" onClick={e => { e.stopPropagation(); moveTask(task.id, columns[columns.findIndex(c => c.id === col.id) - 1].id); }}><ChevronLeft size={14} /></button>}
                      {col.id !== 'erledigt' && <button className="kanban-move-btn" onClick={e => { e.stopPropagation(); moveTask(task.id, columns[columns.findIndex(c => c.id === col.id) + 1].id); }}><ChevronRight size={14} /></button>}
                    </div>
                  </div>
                ))}
                {colTasks.length === 0 && <div className="kanban-empty">Keine Tasks</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Task Modal */}
      <Modal show={showCreateTask} onClose={() => setShowCreateTask(false)} title="Neuen Task erstellen" preventBackdropClose>
        <form onSubmit={handleCreateTask} className="modal-form">
          {formError && <div className="form-error"><AlertCircle size={16} /><span>{formError}</span></div>}
          <div className="form-group"><label>Titel</label><input value={taskForm.title} onChange={e => setTaskForm({...taskForm, title: e.target.value})} required data-testid="task-title-input" /></div>
          <div className="form-group"><label>Beschreibung</label><textarea value={taskForm.description} onChange={e => setTaskForm({...taskForm, description: e.target.value})} rows={3} data-testid="task-desc-input" /></div>
          <div className="form-row">
            <div className="form-group">
              <label>Prioritaet</label>
              <select value={taskForm.priority} onChange={e => setTaskForm({...taskForm, priority: e.target.value})} className="modal-select" data-testid="task-priority">
                <option value="niedrig">Niedrig</option>
                <option value="normal">Normal</option>
                <option value="hoch">Hoch</option>
              </select>
            </div>
            <div className="form-group">
              <label>Deadline (optional)</label>
              <input type="date" value={taskForm.deadline} onChange={e => setTaskForm({...taskForm, deadline: e.target.value})} data-testid="task-deadline" />
            </div>
          </div>
          {adminCoachUsers.length > 0 && (
            <div className="form-group">
              <label>Zuweisen an (optional)</label>
              <select value={taskForm.assignee_id} onChange={e => setTaskForm({...taskForm, assignee_id: e.target.value})} className="modal-select" data-testid="task-assignee">
                <option value="">Nicht zugewiesen</option>
                {adminCoachUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({getRoleLabel(u.role)})</option>
                ))}
              </select>
            </div>
          )}
          <button type="submit" className="btn-login" disabled={formLoading} data-testid="task-submit"><Plus size={16} /> {formLoading ? 'Erstellen...' : 'Task erstellen'}</button>
        </form>
      </Modal>

      {/* Edit Task Modal */}
      <Modal show={showEditTask} onClose={() => setShowEditTask(false)} title={editingTask ? editingTask.title : 'Task bearbeiten'} preventBackdropClose>
        <form onSubmit={handleUpdateTask} className="modal-form">
          {formError && <div className="form-error"><AlertCircle size={16} /><span>{formError}</span></div>}
          <div className="form-group"><label>Beschreibung</label><textarea value={editTaskForm.description} onChange={e => setEditTaskForm({...editTaskForm, description: e.target.value})} rows={3} data-testid="edit-task-desc" /></div>
          <div className="form-row">
            <div className="form-group">
              <label>Prioritaet</label>
              <select value={editTaskForm.priority} onChange={e => setEditTaskForm({...editTaskForm, priority: e.target.value})} className="modal-select" data-testid="edit-task-priority">
                <option value="niedrig">Niedrig</option>
                <option value="normal">Normal</option>
                <option value="hoch">Hoch</option>
              </select>
            </div>
            <div className="form-group">
              <label>Deadline</label>
              <input type="date" value={editTaskForm.deadline} onChange={e => setEditTaskForm({...editTaskForm, deadline: e.target.value})} data-testid="edit-task-deadline" />
            </div>
          </div>
          <div className="form-group">
            <label>Zugewiesen an</label>
            <select value={editTaskForm.assignee_id} onChange={e => setEditTaskForm({...editTaskForm, assignee_id: e.target.value})} className="modal-select" data-testid="edit-task-assignee">
              <option value="">Nicht zugewiesen</option>
              {adminCoachUsers.map(u => (
                <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({getRoleLabel(u.role)})</option>
              ))}
            </select>
          </div>
          {editingTask?.updates && editingTask.updates.length > 0 && (
            <div className="task-updates-list">
              <label>Bisherige Updates</label>
              {editingTask.updates.map((upd, i) => (
                <div key={`upd-${i}-${upd.timestamp}`} className="task-update-entry">
                  <div className="task-update-meta"><strong>{upd.author}</strong> <span>{upd.timestamp ? new Date(upd.timestamp).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span></div>
                  <p>{upd.text}</p>
                </div>
              ))}
            </div>
          )}
          <div className="form-group">
            <label>Neues Update hinzufuegen</label>
            <textarea value={newUpdate} onChange={e => setNewUpdate(e.target.value)} rows={2} placeholder="Update oder Notiz schreiben..." data-testid="task-new-update" />
          </div>
          <button type="submit" className="btn-login" disabled={formLoading} data-testid="edit-task-submit"><Save size={16} /> {formLoading ? 'Speichern...' : 'Speichern'}</button>
        </form>
      </Modal>
    </div>
  );
}
